import { AuxiliaryEsimOrderPhaseEnum } from '../../components/AuxiliaryEsimOrderScene/auxiliaryEsimOrderEnums';
import { ELISA_ID_IFRAME_ID, ElisaIdSessionChecker } from './ElisaIdSessionChecker';
import { EMPLOYEE_LOGOUT_PARAM } from '../../common/constants/pathVariables.js';
import {
  clearMultisimSubscriptionToken,
  getAuthState,
  getMultisimSubscriptionToken,
  setPrimaryMdmId,
} from '../../selfservice/common/localStorageUtils.js';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { deepEqual } from '../../common/utils/objectUtils.js';
import { defaultValues as emptyAuthService, useAuthService } from './authService';
import { fetchExternalAuthMethods } from '../../common/fetch.js';
import { initiateElisaIdV2LoginWithNoPrompt, removeElisaIdQueryParams } from '../common/util/elisaIdLogin.js';
import { isCustomerAdmin, isElisaIdV3Enabled, isSameOrigin } from './authUtils';
import { isEmployeePortal, isEmployeePublicPath } from '../../common/utils/browserUtils.js';
import { isInBrowser } from '../../common/utils/ssrUtils';
import { produce } from 'immer';
import { setAuxiliaryEsimOrderPhase } from '../../selfservice/actions';
import { stripActions } from '../../common/utils/stateUtils';
import { useChangesPoller } from '../../common/hooks/useChangesPoller.js';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import type {
  AnonymousLeadsState,
  AuthorizationRequestState,
  ExternalAuthenticationState,
} from '../../common/types/states.js';
import type { AnonymousUser, AuthenticatedUser, User } from './authState';
import type { AuthServiceProps } from './authService';
import type { AuthenticationMethod } from '../../generated/api/models.js';
import type { ElisaIdSessionCheckResultMessage } from './ElisaIdSessionChecker';
import type { ReactNode } from 'react';
import type { State } from '../../selfservice/common/store.js';

export interface AuthProps extends AuthServiceProps {
  anonymousUser?: AnonymousUser | null | undefined;
  authenticatedUser?: AuthenticatedUser | undefined;
  leadsState?: AnonymousLeadsState | null | undefined;
  authorizationRequest?: AuthorizationRequestState | null;
  externalAuthMethods: AuthenticationMethod[] | 'failed' | 'loading';
  externalAuthentication?: ExternalAuthenticationState | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  resetLoginForm: boolean;
  ssoSessionValid: boolean;
  elisaIdV2LoginProcessing?: boolean | undefined;
  preElisaIdV2AuthLoading: boolean;
  elisaIdV3Enabled: boolean;
}

export const defaultValues: AuthProps = {
  ...emptyAuthService,
  anonymousUser: undefined,
  leadsState: undefined,
  authorizationRequest: undefined,
  externalAuthMethods: [],
  externalAuthentication: undefined,
  isAuthenticated: false,
  isLoading: false,
  resetLoginForm: false,
  ssoSessionValid: false,
  elisaIdV2LoginProcessing: undefined,
  preElisaIdV2AuthLoading: true,
  elisaIdV3Enabled: false,
};

/**
 * @deprecated This exists solely for TestProvider and should not be used elsewhere.
 *             Once all auth data and functionality is moved away from redux this can be deleted.
 **/
// eslint-disable-next-line @typescript-eslint/naming-convention
export const useReduxAuthState_DO_NOT_USE = () => {
  const leadsState = useSelector((state: State) => stripActions(state.user?.anonymous), deepEqual) || undefined;
  const authorizationRequest = useSelector((state: State) => state.authentication?.authorizationRequest, deepEqual);
  const externalAuthentication = useSelector((state: State) => state.authentication?.externalAuthentication, deepEqual);

  return {
    leadsState,
    authorizationRequest,
    externalAuthentication,
  };
};

// NOTE!
// - DO NOT use the `AuthContext` outside of this file, except for tests. It is only exported for tests.
// - We have to use default value because `AuthProvider` is placed very high in the React tree and during errors we render
//   `<RootErrorBoundary>` which uses `AuthProvider`. When that happens the `AuthProvider` is not rendered, thus a default
//   context needs to exist so that  `useAuth()` still works. So the above default values should be reasonable ones.
export const AuthContext = createContext(defaultValues);

export const AuthProvider = ({ children }: { children?: ReactNode }) => {
  const reduxAuth = useReduxAuthState_DO_NOT_USE();
  const [user, setUser] = useState<User>({ anonymous: {} });
  const [extAuthMethods, setExtAuthMethods] = useState<AuthenticationMethod[] | 'failed' | 'loading'>('loading');
  const dispatch = useDispatch();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const authValidateDispatched = useRef(false);
  const elisaIdV3Enabled = isElisaIdV3Enabled();
  const [elisaIdSessionCheckResultReceived, setElisaIdSessionCheckResultReceived] = useState(false);
  const isElisaIdIframe = isInBrowser() && window.self !== window.top && window.frameElement?.id === ELISA_ID_IFRAME_ID;

  useChangesPoller(user.authenticated);

  useEffect(() => {
    const fn = async () => setExtAuthMethods(await fetchExternalAuthMethods());
    void fn();
  }, []);

  const setUserState = useCallback((mutation: (user: User) => void): void => {
    setUser(current => produce(current, mutation));
  }, []);

  const ssoSessionValid = !!user.anonymous.ssoSessionValid;
  const resetLoginForm = !!user.anonymous.resetLoginForm;
  const { companyName, firstName, userRole } = user.authenticated || {};
  const isAuthenticated = Boolean(companyName && firstName && isCustomerAdmin(userRole));
  const isLoading =
    !!user.authenticated?.isFetching ||
    user.anonymous.ssoSessionValid === undefined ||
    (elisaIdV3Enabled && !isAuthenticated && !elisaIdSessionCheckResultReceived);
  const elisaIdV2LoginProcessing = user.anonymous.elisaIdV2LoginProcessing;
  const preElisaIdV2AuthLoading = !(
    user.anonymous?.ssoSessionValid !== undefined || elisaIdV2LoginProcessing === false
  );
  const { processElisaIdLogin, validateAuth, ...authService } = useAuthService(user, setUserState);

  const isBeforeAuthProcessing = elisaIdV2LoginProcessing === undefined && !ssoSessionValid;
  const isNotLogoutUrl = search !== EMPLOYEE_LOGOUT_PARAM;

  const handleElisaIdSessionResult = useCallback(
    (sessionValid: boolean) => {
      setElisaIdSessionCheckResultReceived(true);
      if (sessionValid && !isAuthenticated) {
        validateAuth();
      }
    },
    [validateAuth, isAuthenticated]
  );

  useEffect(() => {
    if (elisaIdV3Enabled) {
      return;
    }
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.get('code') && searchParams.get('state') === getAuthState()) {
      if (getMultisimSubscriptionToken()) {
        dispatch(setAuxiliaryEsimOrderPhase(AuxiliaryEsimOrderPhaseEnum.VALIDATE, getMultisimSubscriptionToken()!));
        clearMultisimSubscriptionToken();
      }
      processElisaIdLogin(searchParams.get('code')!);
      removeElisaIdQueryParams(window.location.pathname, navigate);
    } else if (
      isEmployeePortal(pathname) &&
      !isEmployeePublicPath(pathname) &&
      isBeforeAuthProcessing &&
      searchParams.get('error') !== 'login_required'
    ) {
      if (isNotLogoutUrl) {
        void initiateElisaIdV2LoginWithNoPrompt();
      }
    } else if (!authValidateDispatched.current && !elisaIdV2LoginProcessing) {
      validateAuth();
      authValidateDispatched.current = true;
    }
    if (searchParams.get('error')) {
      removeElisaIdQueryParams(window.location.pathname, navigate);
    }
  }, [
    dispatch,
    isBeforeAuthProcessing,
    navigate,
    pathname,
    elisaIdV2LoginProcessing,
    isNotLogoutUrl,
    processElisaIdLogin,
    validateAuth,
    elisaIdV3Enabled,
  ]);

  useEffect(() => {
    if (elisaIdV3Enabled && !isElisaIdIframe && !authValidateDispatched.current) {
      validateAuth();
      authValidateDispatched.current = true;
    }
  }, [elisaIdV3Enabled, validateAuth, isElisaIdIframe]);

  useEffect(() => {
    if (isElisaIdIframe && isSameOrigin()) {
      const message: ElisaIdSessionCheckResultMessage = {
        type: 'elisaIdSessionCheckResult',
        authenticated: !window.location.search.includes('auth_error=login_required'),
      };
      window.parent.postMessage(message, window.location.origin);
    }
  }, [isElisaIdIframe]);

  useEffect(() => {
    const primaryAccount = user.authenticated?.contact?.accountContactRelationships?.find(acr => acr.isPrimaryAccount);
    if (primaryAccount) {
      setPrimaryMdmId(primaryAccount.accountMasterId);
    }
  }, [user.authenticated]);

  if (isElisaIdIframe) {
    return null;
  }

  return (
    <AuthContext.Provider
      value={{
        ...reduxAuth,
        ...authService,
        processElisaIdLogin,
        validateAuth,
        anonymousUser: user.anonymous,
        authenticatedUser: user.authenticated,
        isAuthenticated,
        ssoSessionValid,
        resetLoginForm,
        isLoading,
        elisaIdV2LoginProcessing,
        preElisaIdV2AuthLoading,
        externalAuthMethods: extAuthMethods,
        elisaIdV3Enabled,
      }}
    >
      {children}
      {elisaIdV3Enabled && isInBrowser() && !isAuthenticated && user.isValidating === false && (
        <ElisaIdSessionChecker handleElisaIdSessionResult={handleElisaIdSessionResult} />
      )}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthProps => {
  return useContext(AuthContext);
};
